import { useEffect, useRef } from 'react'
import { Alert } from '@/components/common/Ui'

/**
 * A panel that slides in from the right for a short form.
 *
 * The alternative — pushing a form in above the table — moves every row down
 * the moment you click Edit, so the row you were aiming at is no longer where
 * you left it. A drawer leaves the list where it is and puts the form beside
 * it.
 *
 * Presentation only: it renders whatever children it is given and reports
 * when it wants to close. Submitting, validating and saving stay with the
 * page, exactly as they were.
 */
export default function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  // Shown at the top of the panel: a save error belongs where the person is
  // looking, not on the page behind the backdrop.
  error,
  onErrorDismiss,
  // 'wide' for forms with more than a couple of fields, so the grid
  // inside gets two columns instead of one cramped one.
  size = 'default',
}) {
  const panelRef = useRef(null)
  const openerRef = useRef(null)

  // Callers write `onClose={() => setForm(null)}`, which is a new function on
  // every render. Depending on it would tear this effect down and set it up
  // again on every keystroke — and since it moves focus, the caret would jump
  // back to the first field mid-sentence. The ref keeps the latest callback
  // without making the effect depend on its identity.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return undefined

    openerRef.current = document.activeElement
    // Focus the first real control, so typing starts where the person looks.
    // Runs once per opening, never again while they type.
    const focusable = panelRef.current?.querySelector(
      'input, select, textarea, button:not(.drawer-close)',
    )
    focusable?.focus()

    const onKey = (event) => {
      if (event.key === 'Escape') onCloseRef.current?.()
    }
    document.addEventListener('keydown', onKey)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      openerRef.current?.focus?.()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.()
      }}
    >
      <aside
        className={`drawer${size === 'wide' ? ' drawer-wide' : ''}`}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        <header className="drawer-head">
          <div>
            <h2 className="drawer-title" id="drawer-title">
              {title}
            </h2>
            {subtitle && <p className="drawer-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm drawer-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="drawer-body">
          <Alert tone="danger" message={error} onClose={onErrorDismiss} />
          {children}
        </div>

        {footer && <footer className="drawer-footer">{footer}</footer>}
      </aside>
    </div>
  )
}
